import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { DemoDataService } from '@core/services/demo-data.service';
import { OKTA_LOGIN_IN_PROGRESS_SESSION_KEY } from '@core/okta-integration/okta-session-keys';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, UserClaims } from '@okta/okta-auth-js';
import {
  BehaviorSubject,
  catchError,
  filter,
  from,
  map,
  merge,
  Observable,
  of,
  throwError,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OktaIntegrationService {
  private _oktaAuth = inject(OKTA_AUTH);
  private _router = inject(Router);
  private _oktaAuthStateService = inject(OktaAuthStateService);
  private _demoDataService = inject(DemoDataService);
  private _analyticsService = inject(AnalyticsService);
  private _demoUserLoggedIn = false;
  /** Tracks the previous emission of getIsAuthenticated() so login/logout fire once, on the
   *  actual false->true / true->false transition, regardless of whether it came from the
   *  demo sign-in flip or a real Okta redirect round-trip. */
  private _wasAuthenticated = false;
  /**
   * Demo mode has no real Okta redirect round-trip (sign-in/out just flips
   * `_demoUserLoggedIn` in place, synchronously, with no navigation), so a
   * one-shot `of(this._demoUserLoggedIn)` snapshot would never notify a
   * consumer subscribed before that flip — e.g. the header, which stays
   * mounted across the whole app rather than re-initializing on every login.
   * This subject makes demo auth state reactive the same way `authState$`
   * already is for real Okta sessions.
   */
  private _demoAuthState$ = new BehaviorSubject<boolean>(false);
  private OKTA_ORIGINAL_URI_KEY = 'okta_original_uri';
  /**
   * Set by signInWithRedirect() right before it hands off to Okta (or flips the demo
   * flag), consumed on the next false->true transition. sessionStorage rather than a field:
   * a real Okta sign-in does a full-page redirect round trip through Okta's hosted page,
   * which tears down and reconstructs this service — an in-memory flag would not survive
   * that, but sessionStorage does. Without this, an already-signed-in user's ordinary page
   * load/refresh (authState$ silently resolving to true from a persisted session, with no
   * sign-in action at all) would be indistinguishable from a real login.
   */
  private static readonly LOGIN_IN_PROGRESS_KEY =
    OKTA_LOGIN_IN_PROGRESS_SESSION_KEY;

  constructor() {
    // Single source of truth for both demo and real Okta auth state (see
    // getIsAuthenticated() above), so login reports identically regardless of path.
    // Logout is deliberately NOT tracked here: on the real path the signOut() redirect
    // tears the page down before a transition-based hit could leave, and the post-return
    // instance starts already-signed-out so no true->false transition is ever observed —
    // trackLogout is fired from signOut() itself instead.
    this.getIsAuthenticated().subscribe(isAuthenticated => {
      if (isAuthenticated && !this._wasAuthenticated) {
        if (this.consumeLoginInProgressFlag()) {
          this._analyticsService.trackLogin('okta');
        }
      }
      this._wasAuthenticated = isAuthenticated;
    });
  }

  private markLoginInProgress(): void {
    try {
      sessionStorage.setItem(OktaIntegrationService.LOGIN_IN_PROGRESS_KEY, '1');
    } catch {
      // Private-mode / disabled storage: the upcoming transition falls back to being
      // treated as a silent restore (login under-reported rather than over-reported).
      console.warn('sessionStorage is not available');
    }
  }

  private consumeLoginInProgressFlag(): boolean {
    try {
      const wasInProgress =
        sessionStorage.getItem(OktaIntegrationService.LOGIN_IN_PROGRESS_KEY) ===
        '1';
      sessionStorage.removeItem(OktaIntegrationService.LOGIN_IN_PROGRESS_KEY);
      return wasInProgress;
    } catch {
      return false;
    }
  }

  /**
   * Merges both sources instead of picking one branch once at subscribe
   * time: `isDemoActive()` reads the router's *current* query params, and a
   * long-lived subscriber (the header, which mounts as part of the app
   * shell before the router finishes its first navigation) can subscribe
   * before `?demo=true` is resolved. Re-checking `isDemoActive()` on every
   * emission — instead of once up front — means a later demo sign-in still
   * reaches a subscriber that locked onto this before demo mode "existed".
   */
  public getIsAuthenticated(): Observable<boolean> {
    return merge(
      this._demoAuthState$.pipe(
        filter(() => this._demoDataService.isDemoActive())
      ),
      this._oktaAuthStateService.authState$.pipe(
        filter(() => !this._demoDataService.isDemoActive()),
        filter((s: AuthState) => !!s),
        map((s: AuthState) => s.isAuthenticated ?? false)
      )
    );
  }

  /**
   * Function that will execute the redirection to the okta hosted page to proceed to the user identification.
   * @param {string} origin - page where the client who presses the `login` button is located
   * @param {string} navigateTo - page previous to redirect
   */
  public signInWithRedirect(
    origin: string,
    navigateTo: string
  ): Observable<void> {
    // See consumeLoginInProgressFlag(): marks this as a real sign-in action so the
    // resulting auth-state transition isn't mistaken for a silent session restore.
    this.markLoginInProgress();

    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = true;
      this._demoAuthState$.next(true);
      return of();
    }

    return from(
      this._oktaAuth.signInWithRedirect({ originalUri: origin })
    ).pipe(
      map(() => {
        void this._router.navigate([navigateTo], {
          queryParamsHandling: 'preserve',
        });
      }),
      catchError((error: unknown) => {
        console.error('Okta sign in error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout function, remove all information okta
   * @param {string} origin
   */
  public signOut(): Observable<void> {
    this._analyticsService.trackLogout('okta');

    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = false;
      this._demoAuthState$.next(false);
      return of();
    }

    return from(this._oktaAuth.signOut()).pipe(
      map(() => {
        const checkoutUrl = location.pathname;
        const urlParameters = new URLSearchParams(globalThis.location.search);
        const token = urlParameters.get('token');
        const checkoutUrlData = JSON.stringify({
          path: checkoutUrl,
          token,
          timestamp: Date.now(),
        });
        localStorage.setItem('checkout-url', checkoutUrlData);
      }),
      catchError((error: unknown) => {
        console.error('Okta sign out error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Return Patron info from Okta
   * @returns Promise<UserClaims>
   */
  public async getUserInfo(): Promise<UserClaims> {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return {
        sub: 'demo-user-123',
        email: 'demo@example.com',
        email_verified: true,
        name: 'Demo User',
        given_name: 'Demo',
        family_name: 'User',
        preferred_username: 'demo@example.com',
        accountCredit: 100_000,
      } as UserClaims;
    }
    return await this._oktaAuth.getUser();
  }

  public getAccessToken(): string {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return 'demo-access-token';
    }
    return this._oktaAuth.getAccessToken()!;
  }
}
